/**
 * Translation utilities for dynamic content
 * This file contains helper functions to translate database content
 * that has pre-defined translations (like default categories)
 */

/**
 * Translates a category name from the database to the current language
 * This handles default categories that have known translations
 * 
 * @param {string} categoryName - The category name from the database
 * @param {function} t - The translation function from i18next
 * @returns {string} - The translated category name or the original if not found
 */
export const translateCategory = (categoryName, t) => {
  // Map of known category translations - using categoryNames namespace
  const categoryMap = {
    // English to translation key mapping
    'Technology': 'categoryNames.technology',
    'Business': 'categoryNames.business',
    'Commerce': 'categoryNames.commerce',
    'Marketing': 'categoryNames.marketing',
    'Entertainment': 'categoryNames.entertainment',
    'News': 'categoryNames.news',
    'Sports': 'categoryNames.sports',
    'Education': 'categoryNames.education',
    'Health': 'categoryNames.health',
    'Lifestyle': 'categoryNames.lifestyle',
    'Finance': 'categoryNames.finance',
    'Gaming': 'categoryNames.gaming',
    'Music': 'categoryNames.music',
    'Travel': 'categoryNames.travel',
    'Food': 'categoryNames.food',
    'Other': 'categoryNames.other',
    // Arabic names also map to the same keys
    'تقنية': 'categoryNames.technology',
    'أعمال': 'categoryNames.business',
    'تجارة': 'categoryNames.commerce',
    'تسويق': 'categoryNames.marketing',
    'ترفيه': 'categoryNames.entertainment',
    'أخبار': 'categoryNames.news',
    'رياضة': 'categoryNames.sports',
    'تعليم': 'categoryNames.education',
    'صحة': 'categoryNames.health',
    'أسلوب حياة': 'categoryNames.lifestyle',
    'مالية': 'categoryNames.finance',
    'ألعاب': 'categoryNames.gaming',
    'موسيقى': 'categoryNames.music',
    'سفر': 'categoryNames.travel',
    'طعام': 'categoryNames.food',
    'أخرى': 'categoryNames.other',
  };

  const translationKey = categoryMap[categoryName];
  if (translationKey) {
    const translated = t(translationKey);
    // If translation exists and is different from the key, use it
    if (translated && translated !== translationKey) {
      return translated;
    }
  }
  
  // Return original if no translation found
  return categoryName;
};

/**
 * Translates an array of categories
 * 
 * @param {string[]} categories - Array of category names
 * @param {function} t - The translation function from i18next
 * @returns {string[]} - Array of translated category names
 */
export const translateCategories = (categories, t) => {
  if (!Array.isArray(categories)) return [];
  return categories.map(cat => translateCategory(cat, t));
};

/**
 * Translates status values commonly used in the application
 * 
 * @param {string} status - The status value
 * @param {function} t - The translation function from i18next
 * @returns {string} - The translated status
 */
export const translateStatus = (status, t) => {
  const statusMap = {
    'active': 'common.active',
    'inactive': 'common.inactive',
    'pending': 'common.pending',
    'completed': 'common.completed',
    'failed': 'common.failed',
    'draft': 'common.draft',
    'published': 'common.published',
    'archived': 'common.archived',
  };

  const translationKey = statusMap[status?.toLowerCase()];
  if (translationKey) {
    const translated = t(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
  }
  
  return status;
};
